import React, { useState, useEffect } from 'react';
import rrService from '../services/rrService';

/**
 * Modal component to display comprehensive account details.
 * Shows cash reserves, trade restrictions, and account information.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether to show the modal
 * @param {Function} props.onClose - Callback to close the modal
 * @param {string} props.accountNumber - Account number to fetch details for
 * @param {string} props.accountName - Account name for display
 * @param {boolean} props.hideDollarValues - Whether to hide dollar values (privacy mode)
 */
// Default cash-like symbols (fallback if API fails)
const DEFAULT_CASH_LIKE_SYMBOLS = ['FDRXX', 'FZFXX', 'FCASH', 'SNSXX', 'SNVXX', 'SPRXX', 'SWVXX', 'SPAXX'];

const AccountDetailsModal = ({ 
  show, 
  onClose, 
  accountNumber, 
  accountName,
  hideDollarValues = false 
}) => {
  const [details, setDetails] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [modelWeights, setModelWeights] = useState({});
  const [totalRebalancingValue, setTotalRebalancingValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collapsedSleeves, setCollapsedSleeves] = useState({});
  const [collapsedModels, setCollapsedModels] = useState({});
  const [cashLikeSymbols, setCashLikeSymbols] = useState(DEFAULT_CASH_LIKE_SYMBOLS);

  /**
   * Format currency values with optional privacy mode.
   */
  const formatCurrency = (value) => {
    if (hideDollarValues) return '$***';
    if (value === null || value === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  /**
   * Format date string for display.
   */
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'None' || dateStr === 'null') return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return '-';
    }
  };

  /**
   * Format percentage or dollar threshold value.
   */
  const formatThreshold = (value, type) => {
    if (!value && value !== 0) return '-';
    const typeStr = (type || '').toLowerCase();
    if (typeStr.includes('percent')) {
      return `${value}%`;
    }
    return formatCurrency(value);
  };

  /**
   * Get badge color for restriction type.
   */
  const getRestrictionBadgeClass = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'hold') return 'bg-warning text-dark';
    if (t === 'unmanaged') return 'bg-secondary';
    if (t === 'sell') return 'bg-danger';
    if (t === 'buy') return 'bg-success';
    if (t === 'weight') return 'bg-info';
    return 'bg-primary';
  };

  /**
   * Format restriction type for display.
   */
  const formatRestrictionType = (type) => {
    const t = (type || '').toLowerCase();
    if (t === 'buy') return 'Buy Only';
    if (t === 'sell') return 'Sell Only';
    return type ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() : '-';
  };

  // Fetch constants (cash-like symbols) on mount
  useEffect(() => {
    const fetchConstants = async () => {
      try {
        const constants = await rrService.getConstants();
        if (constants?.cash_like_symbols?.length > 0) {
          setCashLikeSymbols(constants.cash_like_symbols);
        }
      } catch (err) {
        console.error('Error fetching constants:', err);
        // Keep default symbols on error
      }
    };
    fetchConstants();
  }, []);

  // Fetch details when modal opens
  useEffect(() => {
    if (show && accountNumber) {
      console.log('Fetching details for account:', accountNumber);
      fetchDetails();
    }
  }, [show, accountNumber]);

  /**
   * Fetch account details from the API.
   */
  const fetchDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch details first
      const detailsData = await rrService.getAccountDetails(accountNumber, false);
      setDetails(detailsData);
      
      // Fetch holdings and model weights concurrently
      try {
        const [holdingsData, weightsData] = await Promise.all([
          rrService.getAccountHoldings(accountNumber),
          rrService.getAccountModelWeights(accountNumber).catch(() => null),
        ]);
        console.log('Holdings response:', holdingsData);
        setHoldings(holdingsData?.holdings || []);
        if (weightsData) {
          setModelWeights(weightsData.model_weights || {});
          setTotalRebalancingValue(weightsData.total_rebalancing_value || 0);
        }
      } catch (holdingsErr) {
        console.error('Error fetching holdings (non-blocking):', holdingsErr);
        setHoldings([]);
      }
    } catch (err) {
      console.error('Error fetching account details:', err);
      setError(err.response?.data?.detail || 'Failed to load account details');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  const hasReserves = details?.reserves_count > 0;
  const hasRestrictions = details?.restrictions_count > 0;
  
  // Ensure holdings is always an array
  const holdingsArray = Array.isArray(holdings) ? holdings : [];
  const hasHoldings = holdingsArray.length > 0;

  // Check if there are any real sleeves (non-empty sleeve names)
  const hasRealSleeves = holdingsArray.some(h => h['Sleeve Name'] && h['Sleeve Name'].trim() !== '');
  // Check if there are any sub-models (intermediate level)
  const hasSubModels = holdingsArray.some(h => h['Sub Model Name'] && h['Sub Model Name'].trim() !== '');

  // Group holdings by Sleeve -> Sub Model -> Security Model -> Symbol hierarchy
  // If no real sleeves exist, skip sleeve grouping and just show by model
  // Assets without a model go into "Unassigned" sleeve
  const holdingsBySleeve = holdingsArray.reduce((acc, holding) => {
    const securityModelName = holding['Security Level Model Name'] || '';
    const subModelName = holding['Sub Model Name'] || '';
    const symbol = holding.Symbol || '';
    
    // Skip cash-like symbols
    const isCash = symbol.toUpperCase().includes('CASH') || 
                   symbol.toUpperCase() === 'MARGIN' ||
                   cashLikeSymbols.map(s => s.toUpperCase()).includes(symbol.toUpperCase());
    
    // Determine sleeve name
    let sleeveName;
    if (!securityModelName || securityModelName.trim() === '') {
      // No model assigned - this is unassigned (unless it's cash)
      sleeveName = isCash ? 'Cash' : 'Unassigned';
    } else if (hasRealSleeves) {
      // Has real sleeves - use the sleeve name or 'Other'
      sleeveName = (holding['Sleeve Name'] && holding['Sleeve Name'].trim() !== '') 
        ? holding['Sleeve Name'] 
        : 'Other';
    } else {
      // Flat model - no sleeve hierarchy, use empty string
      sleeveName = '';
    }
    
    // Determine sub-model name (intermediate level)
    const displaySubModel = (hasSubModels && subModelName && subModelName.trim() !== '') 
      ? subModelName 
      : '';
    
    // Security model is the lowest level (groups symbols)
    const displaySecurityModel = securityModelName || symbol || 'Unknown';
    
    if (!acc[sleeveName]) {
      acc[sleeveName] = {};
    }
    if (!acc[sleeveName][displaySubModel]) {
      acc[sleeveName][displaySubModel] = {};
    }
    if (!acc[sleeveName][displaySubModel][displaySecurityModel]) {
      acc[sleeveName][displaySubModel][displaySecurityModel] = [];
    }
    acc[sleeveName][displaySubModel][displaySecurityModel].push(holding);
    return acc;
  }, {});

  // Calculate sleeve totals and sort sleeves by value descending
  const sortedSleeves = Object.entries(holdingsBySleeve)
    .map(([sleeveName, subModels]) => {
      const sleeveTotal = Object.values(subModels).flatMap(sm => Object.values(sm).flat()).reduce((sum, h) => sum + (h['Holdings Current Value'] || 0), 0);
      // Sort sub-models within sleeve by value descending
      const sortedSubModels = Object.entries(subModels)
        .map(([subModelName, securityModels]) => {
          const subModelTotal = Object.values(securityModels).flat().reduce((sum, h) => sum + (h['Holdings Current Value'] || 0), 0);
          // Sort security models within sub-model by value descending
          const sortedSecurityModels = Object.entries(securityModels).sort((a, b) => {
            const aTotal = a[1].reduce((sum, h) => sum + (h['Holdings Current Value'] || 0), 0);
            const bTotal = b[1].reduce((sum, h) => sum + (h['Holdings Current Value'] || 0), 0);
            return bTotal - aTotal;
          });
          return { subModelName, securityModels: sortedSecurityModels, total: subModelTotal };
        })
        .sort((a, b) => b.total - a.total);
      return { sleeveName, subModels: sortedSubModels, total: sleeveTotal };
    })
    .sort((a, b) => b.total - a.total);

  // Toggle functions for collapse/expand
  const toggleSleeve = (sleeveName) => {
    setCollapsedSleeves(prev => ({ ...prev, [sleeveName]: !prev[sleeveName] }));
  };

  const toggleSubModel = (subModelKey) => {
    setCollapsedModels(prev => ({ ...prev, [subModelKey]: !prev[subModelKey] }));
  };

  const toggleModel = (modelKey) => {
    setCollapsedModels(prev => ({ ...prev, [modelKey]: !prev[modelKey] }));
  };

  return (
    <div 
      className="modal fade show" 
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-person-badge me-2"></i>
              Account Details
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            {/* Account Info Header */}
            <div className="alert alert-light border mb-3">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <strong className="text-primary">{accountName || details?.account_name || 'Unknown Account'}</strong>
                  <div className="small text-muted">{accountNumber}</div>
                </div>
                <div className="col-md-3 text-center">
                  <small className="text-muted">Account Type</small>
                  <div className="fw-bold">{details?.account_type || '-'}</div>
                </div>
                <div className="col-md-3 text-center">
                  <small className="text-muted">Model</small>
                  <div className="fw-bold">{details?.model_name || '-'}</div>
                </div>
                <div className="col-md-3 text-md-end">
                  <small className="text-muted">Account Value</small>
                  <div className="fw-bold">{formatCurrency(details?.account_current_value)}</div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading account details...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* No Data Found */}
            {!isLoading && !error && !hasReserves && !hasRestrictions && !hasHoldings && (
              <div className="alert alert-info">
                <i className="bi bi-info-circle me-2"></i>
                No cash reserves, trade restrictions, or holdings found for this account.
              </div>
            )}

            {/* Summary Card - Only show if there are reserves */}
            {!isLoading && !error && hasReserves && (
              <div className="card mb-3">
                <div className="card-header bg-light">
                  <strong>Cash Summary</strong>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-between text-center">
                    <div>
                      <div className="text-muted small">Total Reserve Target</div>
                      <div className="fs-5 fw-bold text-primary">
                        {formatCurrency(details.summary?.total_reserve_target)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted small">Current Cash</div>
                      <div className="fs-5 fw-bold">
                        {formatCurrency(details.summary?.current_cash)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted small">Tradeable Cash</div>
                      <div className="fs-5 fw-bold">
                        {formatCurrency(details.summary?.tradeable_cash)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted small">Sweep Cash</div>
                      <div className="fs-5 fw-bold">
                        {formatCurrency(details.summary?.sweep_cash)}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted small">Difference</div>
                      <div className={`fs-5 fw-bold ${
                        details.summary?.cash_difference >= 0 ? 'text-success' : 'text-danger'
                      }`}>
                        {details.summary?.cash_difference >= 0 ? '+' : ''}
                        {formatCurrency(details.summary?.cash_difference)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cash Reserves Section */}
            {!isLoading && !error && hasReserves && (
              <div className="card mb-3">
                <div className="card-header bg-light">
                  <strong>
                    <i className="bi bi-cash-stack me-2"></i>
                    Cash Reserves ({details.reserves_count})
                  </strong>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-secondary">
                        <tr>
                          <th style={{ width: '18%' }}>Category</th>
                          <th className="text-end" style={{ width: '15%' }}>Calculated Reserve</th>
                          <th className="text-center" style={{ width: '10%' }}>Thresholds</th>
                          <th style={{ width: '13%' }}>Dates</th>
                          <th style={{ width: '36%' }}>Note</th>
                          <th className="text-center" style={{ width: '8%' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.reserves?.map((reserve, idx) => (
                          <tr key={idx} className={reserve.ignored ? 'table-light text-muted' : ''}>
                            <td>
                              <div className="fw-bold">
                                {reserve.cash_reserve_category || 'Default'}
                              </div>
                              {reserve.recurring && reserve.recurring.toLowerCase() !== 'no' && (
                                <span className="badge bg-info text-white" style={{ fontSize: '0.65rem' }}>
                                  Recurring
                                </span>
                              )}
                            </td>
                            <td 
                              className="text-end fw-bold text-primary"
                              style={{ cursor: 'help' }}
                              title={reserve.cash_reserve_value_type === 'PercentOfAccount' 
                                ? `${reserve.cash_reserve}% of ${formatCurrency(reserve.account_current_value)} (Percent of Account)`
                                : `${formatCurrency(reserve.cash_reserve)} (Fixed Dollar Amount)`
                              }
                            >
                              {formatCurrency(reserve.calculated_reserve_dollar)}
                              <i className="bi bi-info-circle ms-1 text-muted" style={{ fontSize: '0.7rem' }}></i>
                            </td>
                            <td className="text-center">
                              <div style={{ fontSize: '0.75rem' }}>
                                <div>
                                  <span className="text-danger">-</span>
                                  {formatThreshold(reserve.lower_threshold, reserve.lower_threshold_type)}
                                </div>
                                <div>
                                  <span className="text-success">+</span>
                                  {formatThreshold(reserve.upper_threshold, reserve.upper_threshold_type)}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div style={{ fontSize: '0.8rem' }}>
                                {reserve.start_date && reserve.start_date !== '-' && (
                                  <div>
                                    <span className="text-muted">Start:</span> {formatDate(reserve.start_date)}
                                  </div>
                                )}
                                {reserve.end_date && reserve.end_date !== '-' && (
                                  <div>
                                    <span className="text-muted">End:</span> {formatDate(reserve.end_date)}
                                  </div>
                                )}
                                {(!reserve.start_date || reserve.start_date === '-') && 
                                 (!reserve.end_date || reserve.end_date === '-') && (
                                  <span className="text-muted">-</span>
                                )}
                              </div>
                            </td>
                            <td>
                              {reserve.note ? (
                                <span style={{ fontSize: '0.8rem' }}>
                                  {reserve.note}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="text-center">
                              {reserve.ignored ? (
                                <span className="badge bg-danger" style={{ fontSize: '0.65rem', cursor: 'help' }}
                                  title={reserve.ignored_until
                                    ? `Excluded from cash reserves until the end of the quarter, we activate on ${new Date(reserve.ignored_until + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                    : 'Excluded from cash reserves until the end of the quarter'}>
                                  Ignored
                                </span>
                              ) : (
                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>Active</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Trade Restrictions Section */}
            {!isLoading && !error && hasRestrictions && (
              <div className="card">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <strong>
                    <i className="bi bi-shield-exclamation me-2"></i>
                    Trade Restrictions ({details.restrictions_count})
                  </strong>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-secondary">
                        <tr>
                          <th style={{ width: '15%' }}>Symbol</th>
                          <th style={{ width: '10%' }}>Type</th>
                          <th style={{ width: '10%' }}>Scope</th>
                          <th style={{ width: '45%' }}>Note</th>
                          <th style={{ width: '20%' }}>Added</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.restrictions?.map((restriction, idx) => (
                          <tr key={idx}>
                            <td>
                              <span className="fw-bold">{restriction.symbol}</span>
                            </td>
                            <td>
                              <span className={`badge ${getRestrictionBadgeClass(restriction.restriction_type)}`}>
                                {formatRestrictionType(restriction.restriction_type)}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${restriction.restriction_source === 'Global' ? 'bg-info' : 'bg-secondary'}`}
                                    title={restriction.restriction_source === 'Global' ? 'Applies to all accounts holding this security' : 'Specific to this account'}>
                                {restriction.restriction_source || 'Personal'}
                              </span>
                            </td>
                            <td>
                              {restriction.restriction_note ? (
                                <span style={{ fontSize: '0.85rem' }}>
                                  {restriction.restriction_note}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <div style={{ fontSize: '0.8rem' }}>
                                {restriction.note_date && restriction.note_date !== '-' ? (
                                  <>
                                    <div>{formatDate(restriction.note_date)}</div>
                                    {restriction.note_added_by && (
                                      <div className="text-muted small">{restriction.note_added_by}</div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Current Holdings Section */}
            {!isLoading && !error && hasHoldings && (
              <div className="card mb-3">
                <div className="card-header bg-light d-flex justify-content-between align-items-center">
                  <strong>
                    <i className="bi bi-briefcase me-2"></i>
                    Current Holdings ({holdingsArray.length} positions)
                  </strong>
                  <span className="badge bg-primary">
                    {formatCurrency(holdingsArray.reduce((sum, h) => sum + (h['Holdings Current Value'] || 0), 0))}
                  </span>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table table-sm table-hover mb-0">
                      <thead className="table-secondary sticky-top">
                        <tr>
                          <th style={{ width: '26%' }}>Symbol / Model / Sleeve</th>
                          <th className="text-end" style={{ width: '10%' }}>Quantity</th>
                          <th className="text-end" style={{ width: '8%' }}>Price</th>
                          <th className="text-end" style={{ width: '13%' }}>Value</th>
                          <th className="text-end" style={{ width: '17%' }}>Target %</th>
                          <th className="text-end" style={{ width: '23%' }}>Actual %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedSleeves.map(({ sleeveName, subModels, total }) => {
                          // Determine if this sleeve should show a header
                          // Show header for: Unassigned, Cash, or any real sleeve name
                          const showSleeveHeader = sleeveName === 'Unassigned' || sleeveName === 'Cash' || (sleeveName && sleeveName !== '');
                          // Get appropriate icon for the sleeve
                          const sleeveIcon = sleeveName === 'Unassigned' ? 'bi-question-circle' : 
                                            sleeveName === 'Cash' ? 'bi-cash-stack' : 'bi-collection';
                          // Get appropriate color for the sleeve header
                          const sleeveHeaderClass = sleeveName === 'Unassigned' ? 'table-warning' : 
                                                   sleeveName === 'Cash' ? 'table-success' : 'table-primary';
                          // Count total positions in this sleeve
                          const sleevePositionCount = subModels.reduce((sum, sm) => 
                            sum + sm.securityModels.reduce((smSum, [, holdings]) => smSum + holdings.length, 0), 0);

                          // Compute weight info for sleeve: aggregate all SLMs within
                          const sleeveSlmNames = new Set(subModels.flatMap(sm => sm.securityModels.map(([name]) => name)));
                          const sleeveTargetPct = [...sleeveSlmNames].reduce((s, n) => s + (modelWeights[n]?.target_weight || 0), 0);
                          const sleeveActualPct = [...sleeveSlmNames].reduce((s, n) => s + (modelWeights[n]?.actual_weight || 0), 0);
                          const sleeveMinPct    = [...sleeveSlmNames].reduce((s, n) => s + (modelWeights[n]?.min_target_weight || 0), 0);
                          const sleeveMaxPct    = [...sleeveSlmNames].reduce((s, n) => s + (modelWeights[n]?.max_target_weight || 0), 0);
                          const sleeveAcctPct   = totalRebalancingValue > 0 ? (total / totalRebalancingValue * 100) : 0;
                          const hasSleeveWeights = sleeveTargetPct > 0 || sleeveActualPct > 0;
                          
                          return (
                          <React.Fragment key={sleeveName || '_no_sleeve_'}>
                            {/* Sleeve header row - show for named sleeves including Unassigned and Cash */}
                            {showSleeveHeader && (
                              <tr 
                                className={sleeveHeaderClass}
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleSleeve(sleeveName)}
                              >
                                <td colSpan="3" className="fw-bold" style={{ fontSize: '0.9rem' }}>
                                  <i className={`bi ${collapsedSleeves[sleeveName] ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
                                  <i className={`bi ${sleeveIcon} me-1`}></i>
                                  {sleeveName}
                                  <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7rem' }}>
                                    {sleevePositionCount} positions
                                  </span>
                                </td>
                                <td className="text-end fw-bold" style={{ fontSize: '0.9rem' }}>
                                  {formatCurrency(total)}
                                </td>
                                <td className="text-end" style={{ fontSize: '0.8rem' }}>
                                  {hasSleeveWeights ? (
                                    <>
                                      <div className="fw-bold">{sleeveTargetPct.toFixed(1)}% <span className="text-muted fw-normal" style={{ fontSize: '0.75em' }}>({sleeveMinPct.toFixed(1)}–{sleeveMaxPct.toFixed(1)}%)</span></div>
                                    </>
                                  ) : '–'}
                                </td>
                                <td className="text-end" style={{ fontSize: '0.8rem' }}>
                                  {hasSleeveWeights ? (
                                    <>
                                      <div className="fw-bold">{sleeveActualPct.toFixed(1)}% <span className="text-muted fw-normal" style={{ fontSize: '0.75em' }}>({sleeveAcctPct.toFixed(1)}% from acct)</span></div>
                                    </>
                                  ) : '–'}
                                </td>
                              </tr>
                            )}
                            
                            {/* Sub-models within this sleeve - show if no sleeve header, or if sleeve is expanded */}
                            {(!showSleeveHeader || !collapsedSleeves[sleeveName]) && subModels.map(({ subModelName, securityModels, total: subModelTotal }) => {
                              const subModelKey = `${sleeveName || 'root'}-${subModelName || 'direct'}`;
                              // Check if sub-model is different from sleeve (show sub-model level only if distinct)
                              const showSubModelHeader = subModelName && subModelName !== '' && subModelName !== sleeveName;
                              // Adjust padding based on levels
                              const subModelPadding = showSleeveHeader ? 'ps-3' : 'ps-2';
                              const securityModelPadding = showSleeveHeader && showSubModelHeader ? 'ps-4' : showSleeveHeader || showSubModelHeader ? 'ps-3' : 'ps-2';
                              const symbolPadding = showSleeveHeader && showSubModelHeader ? 'ps-5' : showSleeveHeader || showSubModelHeader ? 'ps-4' : 'ps-3';
                              
                              return (
                                <React.Fragment key={subModelKey}>
                                  {/* Sub-model header row (only if distinct from sleeve) */}
                                  {showSubModelHeader && (
                                    <tr 
                                      className="table-secondary" 
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => toggleSubModel(subModelKey)}
                                    >
                                      <td colSpan="3" className={`${subModelPadding} fw-bold text-dark`} style={{ fontSize: '0.85rem' }}>
                                        <i className={`bi ${collapsedModels[subModelKey] ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
                                        <i className="bi bi-folder2 me-1"></i>
                                        {subModelName}
                                        <span className="badge bg-light text-dark ms-2" style={{ fontSize: '0.65rem' }}>
                                          {securityModels.reduce((sum, [, h]) => sum + h.length, 0)}
                                        </span>
                                      </td>
                                      <td className="text-end fw-bold text-dark" style={{ fontSize: '0.85rem' }}>
                                        {formatCurrency(subModelTotal)}
                                      </td>
                                      {(() => {
                                        const smSlmNames = securityModels.map(([name]) => name);
                                        const smTargetPct = smSlmNames.reduce((s, n) => s + (modelWeights[n]?.target_weight || 0), 0);
                                        const smActualPct = smSlmNames.reduce((s, n) => s + (modelWeights[n]?.actual_weight || 0), 0);
                                        const smMinPct    = smSlmNames.reduce((s, n) => s + (modelWeights[n]?.min_target_weight || 0), 0);
                                        const smMaxPct    = smSlmNames.reduce((s, n) => s + (modelWeights[n]?.max_target_weight || 0), 0);
                                        const smAcctPct   = totalRebalancingValue > 0 ? (subModelTotal / totalRebalancingValue * 100) : 0;
                                        const hasWeights  = smTargetPct > 0 || smActualPct > 0;
                                        return (
                                          <>
                                            <td className="text-end" style={{ fontSize: '0.8rem' }}>
                                              {hasWeights ? (
                                                <>
                                                  <div className="fw-bold">{smTargetPct.toFixed(1)}% <span className="text-muted fw-normal" style={{ fontSize: '0.75em' }}>({smMinPct.toFixed(1)}–{smMaxPct.toFixed(1)}%)</span></div>
                                                </>
                                              ) : '–'}
                                            </td>
                                            <td className="text-end" style={{ fontSize: '0.8rem' }}>
                                              {hasWeights ? (
                                                <>
                                                  <div className="fw-bold">{smActualPct.toFixed(1)}% <span className="text-muted fw-normal" style={{ fontSize: '0.75em' }}>({smAcctPct.toFixed(1)}% from acct)</span></div>
                                                </>
                                              ) : '–'}
                                            </td>
                                          </>
                                        );
                                      })()}
                                    </tr>
                                  )}
                                  
                                  {/* Security models within sub-model */}
                                  {(!showSubModelHeader || !collapsedModels[subModelKey]) && securityModels.map(([securityModelName, modelHoldings]) => {
                                    const secModelKey = `${subModelKey}-${securityModelName}`;
                                    const secModelTotal = modelHoldings.reduce((sum, h) => sum + (h['Holdings Current Value'] || 0), 0);
                                    
                                    return (
                                      <React.Fragment key={secModelKey}>
                                        {/* Security model header row */}
                                        <tr 
                                          className="table-light" 
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => toggleModel(secModelKey)}
                                        >
                                          <td colSpan="3" className={`${securityModelPadding} fw-bold text-primary`} style={{ fontSize: '0.8rem' }}>
                                            <i className={`bi ${collapsedModels[secModelKey] ? 'bi-chevron-right' : 'bi-chevron-down'} me-2`}></i>
                                            <i className="bi bi-file-earmark me-1"></i>
                                            {securityModelName}
                                            <span className="badge bg-light text-dark ms-2" style={{ fontSize: '0.6rem' }}>
                                              {modelHoldings.length}
                                            </span>
                                          </td>
                                          <td className="text-end fw-bold text-primary" style={{ fontSize: '0.8rem' }}>
                                            {formatCurrency(secModelTotal)}
                                          </td>
                                          {(() => {
                                            const slmData   = modelWeights[securityModelName];
                                            const targetPct = slmData?.target_weight || 0;
                                            const actualPct = slmData?.actual_weight || 0;
                                            const minPct    = slmData?.min_target_weight || 0;
                                            const maxPct    = slmData?.max_target_weight || 0;
                                            const acctPct   = totalRebalancingValue > 0 ? (secModelTotal / totalRebalancingValue * 100) : 0;
                                            const hasWeights = targetPct > 0 || actualPct > 0;
                                            return (
                                              <>
                                                <td className="text-end" style={{ fontSize: '0.8rem' }}>
                                                  {hasWeights ? (
                                                    <>
                                                      <div className="fw-bold text-primary">{targetPct.toFixed(1)}% <span className="text-muted fw-normal" style={{ fontSize: '0.75em' }}>({minPct.toFixed(1)}–{maxPct.toFixed(1)}%)</span></div>
                                                    </>
                                                  ) : '–'}
                                                </td>
                                                <td className="text-end" style={{ fontSize: '0.8rem' }}>
                                                  {hasWeights ? (
                                                    <>
                                                      <div className="fw-bold text-primary">{actualPct.toFixed(1)}% <span className="text-muted fw-normal" style={{ fontSize: '0.75em' }}>({acctPct.toFixed(1)}% from acct)</span></div>
                                                    </>
                                                  ) : '–'}
                                                </td>
                                              </>
                                            );
                                          })()}
                                        </tr>
                                        
                                        {/* Individual holdings in this security model */}
                                        {!collapsedModels[secModelKey] && modelHoldings
                                          .sort((a, b) => (b['Holdings Current Value'] || 0) - (a['Holdings Current Value'] || 0))
                                          .map((holding, idx) => {
                                            // Check if this holding has any restrictions (also check Original Symbol for substituted securities)
                                            const holdingRestriction = details.restrictions?.find(r => 
                                              r.symbol === holding.Symbol || 
                                              (holding['Original Symbol'] && r.symbol === holding['Original Symbol'])
                                            );
                                            
                                            return (
                                            <tr key={`${secModelKey}-${idx}`}>
                                              <td className={symbolPadding}>
                                                <span className="fw-bold">{holding.Symbol}</span>
                                                {holding['Original Symbol'] && holding['Original Symbol'] !== holding.Symbol && (
                                                  <span className="text-muted ms-1" style={{ fontSize: '0.85em' }}>({holding['Original Symbol']})</span>
                                                )}
                                                {holdingRestriction && (
                                                  <span className="ms-2">
                                                    <span className={`badge ${getRestrictionBadgeClass(holdingRestriction.restriction_type)} me-1`} style={{ fontSize: '0.65rem' }}>
                                                      {formatRestrictionType(holdingRestriction.restriction_type)}
                                                    </span>
                                                    <span className={`badge ${holdingRestriction.restriction_source === 'Global' ? 'bg-info' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                                      {holdingRestriction.restriction_source || 'Personal'}
                                                    </span>
                                                  </span>
                                                )}
                                              </td>
                                              <td className="text-end">
                                                {holding.Quantity?.toLocaleString(undefined, { maximumFractionDigits: 3 }) || 0}
                                              </td>
                                              <td className="text-end">
                                                {formatCurrency(holding.Price)}
                                              </td>
                                              <td className="text-end fw-bold">
                                                {formatCurrency(holding['Holdings Current Value'])}
                                              </td>
                                              <td></td>
                                              <td></td>
                                            </tr>
                                          );
                                          })}
                                      </React.Fragment>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDetailsModal;
